import { FormattedMessage, useIntl } from 'react-intl'
import { faClose, faShieldCheck } from '@fortawesome/pro-solid-svg-icons'
import { clsx } from 'clsx'

import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  LinkButton,
} from 'Components/_theme'
import { CopyClipboardButton } from 'Components/CopyClipboardButton/CopyClipboardButton'
import { FormatDate } from 'Components/FormatDate/FormatDate'
import { Icon } from 'Components/Icon/Icon'

import { formatDate, formatRelativeTime } from 'Utilities'

import {
  getBatteryAgeMonths,
  getComplianceStateTranslationId,
  getOverallDeviceHealthStatus,
  getStorageFreeGB,
  getStorageFreePct,
  getStorageTotalGB,
  getStorageUsedGB,
  isBatteryHealthWarning,
  isComplianceWarning,
  isEncryptionWarning,
  isStorageWarning,
  isSyncWarning,
} from './deviceHealth.utils'
import { DeviceHealthInfoTooltip } from './DeviceHealthInfoTooltip'
import type { DeviceHealthModalProps } from './DeviceHealthModal.types'
import { DeviceHealthProgressCircle } from './DeviceHealthProgressCircle'
import { DeviceHealthStatusBadge } from './DeviceHealthStatusBadge'

const renderStrong = (chunks: React.ReactNode) => <strong>{chunks}</strong>

export const DeviceHealthModal = (props: DeviceHealthModalProps) => {
  const { deviceInfo, onClose } = props
  const intl = useIntl()

  if (!deviceInfo) {
    return null
  }

  const overallStatus = getOverallDeviceHealthStatus(deviceInfo)
  const usedGB = getStorageUsedGB(deviceInfo)
  const totalGB = getStorageTotalGB(deviceInfo)
  const freeGB = getStorageFreeGB(deviceInfo)
  const freePct = getStorageFreePct(deviceInfo)
  const usedPct =
    totalGB && usedGB !== null ? Math.round((usedGB / totalGB) * 100) : 0
  const isStorageCritical = isStorageWarning(deviceInfo)
  const isSyncCritical = isSyncWarning(deviceInfo)
  const { battery } = deviceInfo
  const batteryAgeMonths = getBatteryAgeMonths(battery?.ageDays)
  const isBatteryWarning = isBatteryHealthWarning(battery)
  const operatingSystem = [deviceInfo.operatingSystem, deviceInfo.osVersion]
    .filter(Boolean)
    .join(' ')
  const management = [deviceInfo.managementState, deviceInfo.managementAgent]
    .filter(Boolean)
    .join(' · ')
  const enrolledUser = deviceInfo.userEmail || deviceInfo.userPrincipalName

  return (
    <Card className="DeviceHealthModal">
      <CardHeader
        color="green"
        icon={faShieldCheck}
        rightContent={
          <div className="d-flex align-items-center gap-2">
            <DeviceHealthStatusBadge status={overallStatus} />

            <LinkButton
              aria-label={intl.formatMessage({ id: 'Close' })}
              className="p-0"
              onClick={onClose}
            >
              <Icon icon={faClose} size={24} />
            </LinkButton>
          </div>
        }
      >
        <FormattedMessage id="DeviceHealth_title" />
      </CardHeader>

      <CardBody>
        <section className="DeviceHealthModal-section">
          <h6 className="DeviceHealthModal-section-title">
            <FormattedMessage id="DeviceHealth_section_access_compliance" />
          </h6>

          <div className="DeviceHealthModal-grid">
            {deviceInfo.complianceState && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_compliance" />
                </span>

                <Badge
                  color={isComplianceWarning(deviceInfo) ? 'orange' : 'green'}
                  variant="secondary"
                >
                  <span
                    className={clsx(
                      'DeviceHealthModal-badge-dot',
                      isComplianceWarning(deviceInfo) &&
                        'DeviceHealthModal-badge-dot--warning',
                    )}
                  />

                  <FormattedMessage
                    id={getComplianceStateTranslationId(
                      deviceInfo.complianceState,
                    )}
                  />
                </Badge>
              </div>
            )}

            {typeof deviceInfo.isEncrypted === 'boolean' && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_encryption" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_encryption_tooltip" />
                </span>

                <Badge
                  color={isEncryptionWarning(deviceInfo) ? 'orange' : 'green'}
                  variant="secondary"
                >
                  <span
                    className={clsx(
                      'DeviceHealthModal-badge-dot',
                      isEncryptionWarning(deviceInfo) &&
                        'DeviceHealthModal-badge-dot--warning',
                    )}
                  />

                  <FormattedMessage
                    id={
                      deviceInfo.isEncrypted
                        ? 'DeviceHealth_encrypted'
                        : 'DeviceHealth_not_encrypted'
                    }
                  />
                </Badge>
              </div>
            )}

            {management && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_management" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  {management}
                </span>
              </div>
            )}

            {deviceInfo.certExpirationAt && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_managed_until" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_managed_until_tooltip" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  <FormatDate value={deviceInfo.certExpirationAt} />
                </span>
              </div>
            )}

            {deviceInfo.ownerType && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_ownership" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  {deviceInfo.ownerType}
                </span>
              </div>
            )}

            {operatingSystem && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_operating_system" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  {operatingSystem}
                </span>
              </div>
            )}
          </div>
        </section>

        {totalGB !== null && usedGB !== null && freeGB !== null && (
          <section className="DeviceHealthModal-section">
            <h6 className="DeviceHealthModal-section-title">
              <FormattedMessage id="DeviceHealth_section_storage" />
            </h6>

            <div className="DeviceHealthModal-storage">
              <div className="DeviceHealthModal-storage-header">
                <span>
                  <FormattedMessage id="DeviceHealth_storage_used" />
                </span>

                <span
                  className={clsx(
                    'DeviceHealthModal-storage-value',
                    isStorageCritical &&
                      'DeviceHealthModal-storage-value--warning',
                  )}
                >
                  <FormattedMessage
                    id="DeviceHealth_storage_used_value"
                    values={{
                      strong: renderStrong,
                      total: totalGB,
                      used: usedGB,
                    }}
                  />
                </span>
              </div>

              <div
                aria-valuemax={100}
                aria-valuemin={0}
                aria-valuenow={usedPct}
                className="DeviceHealthModal-storage-bar"
                role="progressbar"
              >
                <div
                  className={clsx(
                    'DeviceHealthModal-storage-bar-value',
                    isStorageCritical &&
                      'DeviceHealthModal-storage-bar-value--warning',
                  )}
                  style={{ width: `${usedPct}%` }}
                />
              </div>

              <span className="DeviceHealthModal-storage-free">
                <FormattedMessage
                  id="DeviceHealth_storage_free_gb"
                  values={{ free: freeGB, pct: freePct }}
                />
              </span>
            </div>
          </section>
        )}

        {battery && (
          <section className="DeviceHealthModal-section">
            <h6 className="DeviceHealthModal-section-title">
              <FormattedMessage id="DeviceHealth_section_battery" />
            </h6>

            <div className="DeviceHealthModal-battery">
              {battery.healthScore !== null && (
                <DeviceHealthProgressCircle
                  isWarning={isBatteryWarning}
                  label={
                    <FormattedMessage
                      id={
                        isBatteryWarning
                          ? 'DeviceHealth_status_attention'
                          : 'DeviceHealth_status_healthy'
                      }
                    />
                  }
                  value={battery.healthScore}
                />
              )}

              <div className="DeviceHealthModal-battery-rows">
                {battery.healthScore !== null && (
                  <div className="DeviceHealthModal-field DeviceHealthModal-field-row">
                    <span className="DeviceHealthModal-field-label">
                      <FormattedMessage id="DeviceHealth_battery_health_score" />

                      <DeviceHealthInfoTooltip translationId="DeviceHealth_battery_health_score_tooltip" />
                    </span>

                    <span
                      className={clsx(
                        'DeviceHealthModal-field-value',
                        isBatteryWarning &&
                          'DeviceHealthModal-field-value--warning',
                      )}
                    >
                      <FormattedMessage
                        id="DeviceHealth_battery_health_score_value"
                        values={{ score: battery.healthScore }}
                      />
                    </span>
                  </div>
                )}

                {battery.maxCapacityPct !== null && (
                  <div className="DeviceHealthModal-field DeviceHealthModal-field-row">
                    <span className="DeviceHealthModal-field-label">
                      <FormattedMessage id="DeviceHealth_battery_capacity" />

                      <DeviceHealthInfoTooltip translationId="DeviceHealth_battery_capacity_tooltip" />
                    </span>

                    <span className="DeviceHealthModal-field-value">
                      <FormattedMessage
                        id="DeviceHealth_battery_capacity_value"
                        values={{ pct: battery.maxCapacityPct }}
                      />
                    </span>
                  </div>
                )}

                {battery.cycleCount !== null && (
                  <div className="DeviceHealthModal-field DeviceHealthModal-field-row">
                    <span className="DeviceHealthModal-field-label">
                      <FormattedMessage id="DeviceHealth_battery_cycles" />

                      <DeviceHealthInfoTooltip translationId="DeviceHealth_battery_cycles_tooltip" />
                    </span>

                    <span className="DeviceHealthModal-field-value">
                      {battery.cycleCount}
                    </span>
                  </div>
                )}

                {batteryAgeMonths !== null && (
                  <div className="DeviceHealthModal-field DeviceHealthModal-field-row">
                    <span className="DeviceHealthModal-field-label">
                      <FormattedMessage id="DeviceHealth_battery_age" />

                      <DeviceHealthInfoTooltip translationId="DeviceHealth_battery_age_tooltip" />
                    </span>

                    <span className="DeviceHealthModal-field-value">
                      <FormattedMessage
                        id="DeviceHealth_battery_age_value"
                        values={{ months: batteryAgeMonths }}
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        <section className="DeviceHealthModal-section">
          <h6 className="DeviceHealthModal-section-title">
            <FormattedMessage id="DeviceHealth_section_device_details" />
          </h6>

          <div className="DeviceHealthModal-grid">
            {deviceInfo.model && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_model" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  {deviceInfo.model}
                </span>
              </div>
            )}

            <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
              <span className="DeviceHealthModal-field-label">
                <FormattedMessage id="DeviceHealth_serial_number" />
              </span>

              <span className="DeviceHealthModal-field-value d-flex align-items-center">
                {deviceInfo.serialNumber}

                <CopyClipboardButton
                  iconSize={16}
                  value={deviceInfo.serialNumber}
                />
              </span>
            </div>

            {enrolledUser && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_enrolled_user" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  {enrolledUser}
                </span>
              </div>
            )}

            {deviceInfo.enrolledAt && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_enrolled_on" />
                </span>

                <span className="DeviceHealthModal-field-value">
                  <FormatDate value={deviceInfo.enrolledAt} />
                </span>
              </div>
            )}

            {deviceInfo.lastSyncedAt && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_intune_check_in" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_intune_check_in_tooltip" />
                </span>

                <span
                  className={clsx(
                    'DeviceHealthModal-field-value',
                    isSyncCritical && 'DeviceHealthModal-field-value--warning',
                  )}
                >
                  <FormattedMessage
                    id="DeviceHealth_intune_check_in_value"
                    values={{
                      date: formatDate(deviceInfo.lastSyncedAt),
                      time: formatRelativeTime(
                        deviceInfo.lastSyncedAt,
                        intl.locale,
                      ),
                    }}
                  />
                </span>
              </div>
            )}

            {deviceInfo.wifiMacAddress && (
              <div className="DeviceHealthModal-field DeviceHealthModal-field--inline">
                <span className="DeviceHealthModal-field-label">
                  <FormattedMessage id="DeviceHealth_wifi_mac" />

                  <DeviceHealthInfoTooltip translationId="DeviceHealth_wifi_mac_tooltip" />
                </span>

                <span className="DeviceHealthModal-field-value d-flex align-items-center">
                  {deviceInfo.wifiMacAddress}

                  <CopyClipboardButton
                    iconSize={16}
                    value={deviceInfo.wifiMacAddress}
                  />
                </span>
              </div>
            )}
          </div>
        </section>
      </CardBody>
    </Card>
  )
}
